import React, { useMemo, useState, useEffect, useLayoutEffect } from 'react'
import {
  AppBar, Toolbar, Box, Typography, Button, IconButton, TextField, Slide,
  FormControl, Select, MenuItem, Link as MLink, Stack, Fade, Accordion, AccordionSummary, AccordionDetails,
  FormHelperText
} from '@mui/material'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { getMegaNav, ROOT_NAV, buildLangHref, stripLangPrefix } from '@data/nav'
import logo from '@assets/images/logo/logo.svg'
import logoM from '@assets/images/logo/logo_m.svg'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DesktopMacRoundedIcon from '@mui/icons-material/DesktopMacRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useUserRole } from '@hooks/useUserRole'
import { useIsMobile } from '@/hooks/Responsive'
import { useAuth } from '@components/AuthContext';
import { translateText } from '@/i18n/translate';
import { useLang } from '@/i18n/useLang';
import { visuallyHidden } from '@mui/utils'
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'

export default function AppHeader() {
  const { REACT_APP_ADMIN_PAGE_PATH } = process.env;
  //const userRole = useUserRole()
  // const userRole = {
  //   isLoggedIn: true,
  //   role: ['강원대학교 기관학습자', '강원대학교 교수자', '강원대학교 CP'],
  //   userName: '홍길동',
  //   email: 'demo@knou.ac.kr',
  //   courseCount: 12,
  // }
  const [userRole, setUserRole] = useState({
    isLoggedIn: false,
    role: [],
    userName: '',
    email: '',
    courseCount: 0,
  });

  const [megaOpen, setMegaOpen] = useState(false)
  const [hoveredRoot, setHoveredRoot] = useState('')
  const [sitemapOpen, setSitemapOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuHeight, setMenuHeight] = useState(0)
  const [winWidth, setWinWidth] = useState(window.innerWidth)
  const [roleValue, setRoleValue] = useState(userRole.role[0])
  const [mobileActiveIndex, setMobileActiveIndex] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const { isMobile } = useIsMobile()
  const lang = useLang()
  
  const location = useLocation()
  const navigate = useNavigate()
  
  const clearSessionOnNavigate = () => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('setpage')) {
        sessionStorage.setItem(key, '1')
      }
    })
  }
  
  // route 변경 시 hover 상태 초기화
  useEffect(() => {
    setHoveredRoot('')
  }, [location.pathname])

  const currentPath = stripLangPrefix(location.pathname);

  const megaItems = useMemo(() => getMegaNav(), [])
  
  // 검색 관련
  const handleSearch = () => {
    if (searching) return;

    const trimmed = searchKeyword.trim();
    // if (!trimmed) return;
    // 2글자 미만은 return
    if (trimmed.length < 2) {
      setSearchError(true);
      return;
    }

    setSearchError(false);
    setSearching(true);
    const base = buildLangHref('/search', lang);
    navigate(`${base}?keyword=${encodeURIComponent(trimmed)}&tab=all`);
    setSearchOpen(false);   // 검색창 닫기
    setSearchKeyword('');   // 입력값 초기화

    setTimeout(() => setSearching(false), 300);
  };

  // 리사이즈
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // location 바뀌면 사이트맵만 닫기
  useEffect(() => {
    if (sitemapOpen) setSitemapOpen(false)
  }, [location])

  useLayoutEffect(() => {
    const locked = sitemapOpen || searchOpen;

    if (locked) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.scrollbarGutter = 'stable';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
    }
  }, [sitemapOpen, searchOpen]);


  const { user, loading, isAuthReady, logout } = useAuth();
  useEffect(() => {
    // 아직 로딩 중이면 아무 것도 하지 않음
    if (loading) return;

    // 여기서 로그인 안 됐다고 해서 navigate 하지 않음
    if (!user) {
      // 로그아웃 상태 UI 세팅만
      setUserRole({
        isLoggedIn: false,
        role: [],
        userName: '',
        email: '',
        courseCount: 0,
      });
      return;
    }

    setUserRole({
      isLoggedIn: user ? true : false,
      role: [],
      userName: user.name,
      email: user.email,
      courseCount: 0,
    });

  }, [loading, user]);
  
  // 로그아웃
  const handleLogout = async () => {
    try {
      await logout();  // 서버 /api/auth/logout 호출 + 상태 초기화
    } finally {
      window.location.href = buildLangHref("/", lang) // 홈으로 이동 + 전체 새로고침
    }
  };

  // 사이트맵 필터
  const sitemapItems = useMemo(() => {
    if (!Array.isArray(ROOT_NAV)) return []

    const clss = user?.clss ?? ''
    const isProfGroup =
      clss === 'PROFESSOR' ||
      clss === 'ASSISTANT'

    const filterRecursive = (items = []) =>
      items
        .filter(Boolean)
        .filter(item => {
          if (item.hidden) return false
          if (item.roleOnly && item.roleOnly !== userRole) return false

          // 일반 사용자일 때만 professorOnly 숨김
          if (!isProfGroup && item.professorOnly) return false
          // 교수자그룹일시 noProfessor 숨김
          if(isProfGroup && item.noProfessor) return false

          return true
        })
        .map(item => ({
          ...item,
          children: filterRecursive(item.children)
        }))

    return filterRecursive(ROOT_NAV)
  }, [userRole, user])


  // 메가메뉴 높이
  useLayoutEffect(() => {
    if (!megaOpen) return
    const heights = megaItems.map((_, i) => {
      const el = document.getElementById(`mega-${i}`)
      return el?.offsetHeight ?? 0
    })
  //  setMenuHeight(Math.max(...heights, 0))
  }, [megaOpen, megaItems, winWidth])

  // 링크 href
  const getEffectiveHref = (item) => {
    if (item.href) return item.href
    if (Array.isArray(item.children) && item.children.length > 0) return item.children[0].href
    return null
  }

  const [emailOpen, setEmailOpen] = useState(false)
  const [emailOpenMobile, setEmailOpenMobile] = useState(false)

  return (
    <>
      <AppBar
        elevation={0}
        sx={{
          bgcolor: 'common.white',
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        {/* 상단 유틸 */}
        <Box
          sx={{
            display: isMobile ? 'none' : 'block',
            height: '40px',
            bgcolor: 'grey.900',
            color: 'common.white',
          }}
        >
          <Box 
            maxWidth="xl"
            mx="auto"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
            }}
          >
            {/* 로그인 여부 */}
            {userRole?.isLoggedIn ? (
              <Stack direction="row" alignItems="center" spacing={2}>

                <FormControl>
                  <Typography
                    component="label"
                    sx={visuallyHidden}
                    htmlFor="header-util-menu-label"
                  >
                    메뉴 선택
                  </Typography>
                  <Select
                    value=""
                    open={emailOpen}
                    onOpen={() => setEmailOpen(true)}
                    onClose={() => setEmailOpen(false)}
                    displayEmpty
                    input={
                      <OutlinedInput
                        startAdornment={
                          <InputAdornment position="start">
                            <AccountCircleIcon
                              sx={{
                                fontSize: '20px',
                                color: 'common.white',
                              }}
                            />
                          </InputAdornment>
                        }
                      />
                    }
                    inputProps={{
                      'id': 'header-util-menu-label',
                      'labelId': 'header-util-menu-label',
                      'aria-label': '사용자 계정 메뉴',
                    }}
                    sx={{
                      height: '24px',
                      color: 'common.white',
                      bgcolor: 'transparent !important',
                      '& .MuiSelect-select': {
                        px: 0,
                        color: 'common.white',
                        bgcolor: 'transparent !important',
                        display: 'flex',
                        alignItems: 'center',
                        height: '24px',
                        borderColor: 'transparent !important',
                        typography: 'body2',
                      },
                      '& .MuiSvgIcon-root': {
                        color: theme => `${theme.palette.text.contrastText} !important`, 
                        fontSize: '20px',
                        right: 0,
                      },
                      '& fieldset': {
                        borderColor: 'transparent !important',
                      },
                      '&.Mui-expanded': { 
                        color: theme => `${theme.palette.text.contrastText} !important`, 
                        '& .MuiSelect-select': { 
                          color: 'primary.main' 
                        }, 
                        '& .MuiSvgIcon-root': { 
                          color: theme => `${theme.palette.text.contrastText} !important`, 
                          transform: 'rotate(180deg)', 
                        }, 
                      },
                    }}
                    renderValue={() => userRole.email}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          py: 1,
                          borderRadius: 2, 
                          color: 'common.white', 
                          overflow: 'hidden', 
                          '& .MuiMenuItem-root': { 
                            typography: 'body2', 
                            height: '40px', 
                            lineHeight: '40px', 
                            transition: 'background-color 0.2s ease, color 0.2s ease', 
                            '&:hover': { 
                              bgcolor: 'point.50', 
                              color: 'primary.main', 
                            }, 
                            '&.Mui-selected': { 
                              color: 'primary.main', 
                              bgcolor: 'transparent', 
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem 
                      onClick={() => {
                        clearSessionOnNavigate()
                        navigate(
                          buildLangHref(
                            user?.clss === 'PROFESSOR'
                              ? '/mypage/management'
                              : '/mypage/courses',
                            lang
                          )
                        )
                      }}
                    >
                      마이페이지
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>로그아웃</MenuItem>
                  </Select>
                </FormControl>

                {userRole.role?.length > 0 ? (
                <FormControl>
                  <Select
                    value={roleValue}
                    onChange={(e) => setRoleValue(e.target.value)}
                    sx={{
                      height: '24px',
                      color: 'common.white',
                      bgcolor: 'transparent !important',
                      '& .MuiSelect-select': {
                        px: 0,
                        color: 'common.white',
                        bgcolor: 'transparent !important',
                        display: 'flex',
                        alignItems: 'center',
                        height: '24px',
                        borderColor: 'transparent !important',
                        typography: 'body2',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'common.white',
                        fontSize: '20px',
                        right: 0,
                      },
                      '& fieldset': {
                        borderColor: 'transparent !important',
                      },
                      '&.Mui-expanded': { 
                        color: theme => `${theme.palette.text.contrastText} !important`, 
                        '& .MuiSelect-select': { 
                          color: 'primary.main' 
                        }, 
                        '& .MuiSvgIcon-root': { 
                        color: theme => `${theme.palette.text.contrastText} !important`, 
                          transform: 'rotate(180deg)', 
                        }, 
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          py: 1,
                          borderRadius: 2, 
                          color: 'common.white', 
                          overflow: 'hidden', 
                          '& .MuiMenuItem-root': { 
                            typography: 'body2', 
                            height: '40px', 
                            lineHeight: '40px', 
                            transition: 'background-color 0.2s ease, color 0.2s ease', 
                            '&:hover': { 
                              bgcolor: 'point.50', 
                              color: 'primary.main', 
                            }, 
                            '&.Mui-selected': { 
                              color: 'primary.main', 
                              bgcolor: 'transparent', 
                            },
                          },
                        },
                      },
                    }}
                  >
                    {userRole.role.map((role, idx) => (
                      <MenuItem key={idx} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                ) : (
                <></>
                )}

                <Box
                  sx={{
                    width: '1px',
                    height: '13px',
                    bgcolor: 'divider',
                  }}
                />
                {/* 일반, 학생이 아닌경우에만 관리시스템 링크 노출 */}
                {(user?.clss !== 'GENERAL' && user?.clss !== 'STUDENT') && (
                <MLink
                  component={RouterLink}
                  // to={buildLangHref('/management', lang)}
                  to={REACT_APP_ADMIN_PAGE_PATH}
                  target="_blank"
                  underline="none"
                  sx={{ typography: 'body2', color: 'common.white', '&:hover': { color: 'primary.main' } }}
                >
                  관리시스템 바로가기
                </MLink>
                )}
              </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <MLink
                    component={RouterLink}
                    to={buildLangHref('/login', lang)}
                    underline="none"
                    sx={{ typography: 'body2', color: 'common.white', '&:hover': { color: 'primary.main' } }}
                  >
                    로그인
                  </MLink>
                  <Box
                    sx={{
                      width: '1px',
                      height: '13px',
                      bgcolor: 'divider',
                    }}
                  />
                  <MLink
                    component={RouterLink}
                    // to="/signup"
                    onClick={() => (window.location.href = buildLangHref("/signup", lang))}
                    underline="none"
                    sx={{ typography: 'body2', color: 'common.white', '&:hover': { color: 'primary.main' } }}
                  >
                    회원가입
                  </MLink>
                </Stack>
            )}
          </Box>
        </Box>

        {/* GNB */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box maxWidth="xl" mx="auto" width="100%">
            <Toolbar
              disableGutters
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: isMobile ? 'space-between' : 'center',
                alignItems: 'center',
                gap: 2,
                px: 2,
                height: isMobile ? '64px' : '80px',
              }}
            >
              {/* 로고 (모바일 사이트맵 오픈 시 숨김) */}
              <MLink
                component={RouterLink}
                to={buildLangHref('/', lang)}
                onClick={clearSessionOnNavigate}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src={isMobile? logoM : logo}
                  alt="한국방송통신대학교OpenVLab로고"
                  sx={{ height: isMobile ? '34px' : '36px' }}
                />
              </MLink>

              {/* 1Depth (메가메뉴 제거 버전) */}
              <Box
                mx="auto"
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  gap: { lg: 4, xl: 7 },
                  position: 'relative',
                  visibility: sitemapOpen ? 'hidden' : 'visible',
                }}
              >
                {megaItems.map((item, idx) => {
                const hasChildren = Array.isArray(item.children) && item.children.length > 0

                const getAllHrefs = (node) => {
                  if (!node) return []
                  let list = []
                  if (node.href) list.push(node.href)
                  if (Array.isArray(node.children)) {
                    node.children.forEach(child => {
                      list = list.concat(getAllHrefs(child))
                    })
                  }
                  return list
                }

                const hrefList = getAllHrefs(item)

                const active = hrefList.some(h => currentPath.startsWith(h))
                  return (
                    <Box 
                      key={item.label}
                      sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column' }}
                      onFocus={() => setHoveredRoot(item.label)}
                      onMouseEnter={() => setHoveredRoot(item.label)}
                      onMouseLeave={() => setHoveredRoot('')}
                    >
                      {/* 1Depth 버튼 */}
                      <Button
                        component={item.target === '_blank' ? 'a' : RouterLink}
                        href={item.target === '_blank' ? item.href : undefined}
                        to={item.target !== '_blank' ? buildLangHref(item.href, lang) : undefined}
                        target={item.target === '_blank' ? '_blank' : undefined}
                        rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                        onClick={item.target !== '_blank' ? clearSessionOnNavigate : undefined}
                        disableRipple
                        disableFocusRipple
                        data-label={item.label}
                        onFocus={() => setHoveredRoot(item.label)}
                        onMouseEnter={() => setHoveredRoot(item.label)}
                        aria-haspopup={hasChildren ? 'true' : undefined}
                        aria-expanded={hoveredRoot === item.label}
                        sx={{
                          display: 'inline-flex',  
                          alignItems: 'center',
                          gap: 0.5,    
                          py: 0,
                          px: 0,
                          height: '80px',
                          minWidth: 'auto',
                          position: 'relative',
                          textTransform: 'none',
                          typography: 'body1',
                          fontVariationSettings: '"wght" 600',
                          fontWeight: 
                            hoveredRoot === item.label || active
                            ? '600'
                            : '500',
                          color:
                            hoveredRoot === item.label || active
                              ? 'text.primary'
                              : 'text.secondary',
                          '&:hover': {
                            bgcolor: 'background.paper',
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: '3px',
                            bgcolor: 'primary.main',
                            transform:
                              hoveredRoot === item.label || active ? 'scaleX(1)' : 'scaleX(0)',
                            opacity: hoveredRoot === item.label || active ? 1 : 0,
                            transition: 'transform .25s ease, opacity .25s ease',
                            transformOrigin: 'center',
                          },
                        }}
                      >
                        {translateText(item.label)}

                        {item.target === '_blank' && (
                          <OpenInNewIcon
                            fontSize="small"
                            sx={{
                              color: 'grey.dark',
                            }}
                          />
                        )}
                      </Button>

                      {/* 2Depth 드롭다운 */}
                      {hasChildren && (
                        <Box
                          sx={{
                            display: hoveredRoot === item.label ? 'flex' : 'none',
                            flexDirection: 'column',
                            gap: 1,
                            p: 3,
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: '174px',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.12)',
                            zIndex: theme => theme.zIndex.appBar + 5,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.children
                            .filter(l2 => !l2.hidden)
                            .map((l2, i2) => (
                              <MLink
                                key={i2}
                                component={RouterLink}
                                to={buildLangHref(l2.href, lang)}
                                onClick={clearSessionOnNavigate}
                                underline="none"
                                sx={{
                                  display: 'block',
                                  typography: 'body2',
                                  color: 'text.secondary',
                                  textAlign: 'center',
                                  py: 0.5,
                                  '&:hover': {
                                    color: 'primary.main',
                                    bgcolor: 'background.paper'
                                  },
                                }}
                              >
                                {l2.label}
                              </MLink>
                            ))}
                        </Box>
                      )}
                    </Box>
                  )
                })}

                {userRole?.isLoggedIn && (
                  <>
                    {/* 구분자 */}
                    <Box
                      sx={{
                        width: '1px',
                        height: '24px',
                        bgcolor: 'divider',
                      }}
                    />

                    {/* 나의 수강강좌 */}
                    <MLink
                      component={RouterLink}
                      to={buildLangHref(user?.clss === 'PROFESSOR' ? "/mypage/management":"/mypage/courses", lang)}
                      onClick={clearSessionOnNavigate}
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        typography: 'body1',
                        fontWeight: '500',
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      <DesktopMacRoundedIcon fontSize="small" sx={{ color: 'grey.dark'}} />
                      {user?.clss === 'PROFESSOR' ? "강좌관리":"나의 수강강좌"} {/* 교수일 때는 수강관리 */}
                    </MLink>
                  </>
                )}
              </Box>

              {/* 유틸 영역 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, ml: 'auto' }}>
                {/* 모바일: 사이트맵 */}
                {sitemapOpen && (
                  <Box sx={{ display: isMobile ? 'flex' : 'none', alignItems: 'center', gap: 1 }}>
                    <FormControl
                      size="small"
                      variant="standard"
                      sx={{
                        minWidth: '68px',
                        '& .MuiInputBase-root': { backgroundColor: 'transparent !important' },
                      }}
                    >
                    </FormControl>
                  </Box>
                )}

                {/* 로그아웃 */}
                {isMobile && sitemapOpen && userRole?.isLoggedIn && (
                  <IconButton
                    onClick={handleLogout}
                    sx={{
                      p: 0.5,
                      color: 'grey.dark',
                    }}
                  >
                    <LogoutRoundedIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                  </IconButton>
                )}

                {/* 검색 */}
                <IconButton
                  aria-label={searchOpen ? '검색창 닫기' : '검색창 열기'}
                  onClick={() => setSearchOpen(!searchOpen)}
                  sx={{
                    color: 'common.black',
                    p: isMobile ? 0.5 : 0,
                    display: sitemapOpen ? 'none' : 'inline-flex',
                  }}
                >
                  {searchOpen ? <CloseIcon sx={{ fontSize: isMobile ? '24px' : '32px', color: 'grey.dark' }} /> : <SearchIcon sx={{ fontSize: isMobile ? '24px' : '32px', color: 'grey.dark' }} />}
                </IconButton>

                {/* 사이트맵 버튼 */}
                <IconButton
                  aria-label={sitemapOpen ? '사이트맵 닫기' : '사이트맵 열기'}
                  onClick={() => setSitemapOpen(!sitemapOpen)}
                  sx={{
                    color: 'common.black',
                    p: isMobile ? 0.5 : 0,
                    display: 'inline-flex',
                  }}
                >
                  {sitemapOpen ? <CloseIcon sx={{ fontSize: isMobile ? '24px' : '32px', color: 'grey.dark' }} /> : <MenuIcon sx={{ fontSize: isMobile ? '24px' : '32px', color: 'grey.dark' }} />}
                </IconButton>
              </Box>
            </Toolbar>
          </Box>
        </Box>
      </AppBar>

      {/* 검색 */}
      {searchOpen && (
        <Box
          data-search-overlay={searchOpen ? 'true' : 'false'}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: isMobile ? '64px' : '120px',
            overflow: 'hidden',
            pointerEvents: searchOpen ? 'auto' : 'none', 
            zIndex: 11,
          }}
          onClick={() => setSearchOpen(false)}
        >
          <Slide direction="down" in={searchOpen} unmountOnExit={false} timeout={300}>
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'relative',
                bgcolor: 'common.white',
                px: isMobile ? 2 : 4,
                pb: isMobile ? 11 : 15,
                pt: isMobile ? 9 : 15,
                textAlign: 'center',
                width: '100%',
                boxShadow: 3,
                zIndex: (theme) => theme.zIndex.modal + 1,
                
              }}
            >
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 3 }}>
                무엇을 도와드릴까요?
              </Typography>
              <Box sx={{ position: 'relative', maxWidth: '560px', mx: 'auto', }}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  placeholder="검색어를 입력해 주세요"
                  variant="outlined"
                  value={searchKeyword}
                  onChange={(e) => {
                    setSearchKeyword(e.target.value);
                    if (searchError) setSearchError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      px: isMobile ? 1 : 2,
                      pr: isMobile ? 5.5 : 7,
                      height: '64px'
                    },
                  }}
                />
                <IconButton
                  onClick={handleSearch}
                  sx={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <SearchIcon sx={{ fontSize: isMobile ? '24px' : '32px', color: 'grey.dark' }} />
                </IconButton>
              </Box>
              <Box sx={{ position: 'relative', maxWidth: '560px', mx: 'auto', }}>
                {searchError && <FormHelperText error>공백을 제외한 두글자 이상 입력해 주세요.</FormHelperText>}
              </Box>
            </Box>
          </Slide>
        </Box>
      )}

      {/* 사이트맵 */}
      <Fade in={sitemapOpen} timeout={300} unmountOnExit>
        <Box
          role="navigation"
          aria-label="사이트맵 전체"
          sx={{
            position: 'fixed',
            top: isMobile ? '64px' : '120px',
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'common.white',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box
            maxWidth="xl"
            sx={{
              px: isMobile ? 0 : 2,
              mx: 'auto',
              py: isMobile ? 0 : 10,
            }}
          >

            {/* 모바일 상단 사용자 블록 */}
            <Box sx={{ display: isMobile ? 'block' : 'none', px: 0 }}>
              {/* 상단 유틸: 모바일 */}
              <Box
                sx={{
                  display: isMobile && sitemapOpen ? 'flex' : 'none',
                  flexDirection: 'column',
                  bgcolor: 'grey.900',
                  color: 'common.white',
                  px: 2,
                  py: 2,
                }}
              >
                {/* 로그인 여부 */}
                {userRole?.isLoggedIn ? (
                  <Typography>{userRole.email}</Typography>
                  // <Stack direction="column" spacing={0}>
                  //   <FormControl>
                  //     <Typography
                  //       component="label"
                  //       sx={visuallyHidden}
                  //       htmlFor="footer-family-label"
                  //     >
                  //       Family Site 선택
                  //     </Typography>
                  //     <Select
                  //       value=""
                  //       open={emailOpenMobile}
                  //       onOpen={() => setEmailOpenMobile(true)}
                  //       onClose={() => setEmailOpenMobile(false)}
                  //       displayEmpty
                  //       sx={{
                  //         height: '40px',
                  //         color: 'common.white',
                  //         bgcolor: 'transparent !important',
                  //         '& .MuiSelect-select': {
                  //           px: 0,
                  //           color: 'common.white',
                  //           bgcolor: 'transparent !important',
                  //           display: 'flex',
                  //           alignItems: 'center',
                  //           height: '40px',
                  //           borderColor: 'transparent !important',
                  //           typography: 'body2',
                  //         },
                  //         '& .MuiSvgIcon-root': {
                  //           color: theme => `${theme.palette.text.contrastText} !important`, 
                  //           fontSize: '20px',
                  //           right: 0,
                  //         },
                  //         '& fieldset': {
                  //           borderColor: 'transparent !important',
                  //         },
                  //         '&.Mui-expanded': { 
                  //           color: theme => `${theme.palette.text.contrastText} !important`, 
                  //           '& .MuiSelect-select': { 
                  //             color: 'primary.main' 
                  //           }, 
                  //           '& .MuiSvgIcon-root': { 
                  //           color: theme => `${theme.palette.text.contrastText} !important`, 
                  //             transform: 'rotate(180deg)', 
                  //           }, 
                  //         },
                  //       }}
                  //       renderValue={() => userRole.email}
                  //       MenuProps={{
                  //         PaperProps: {
                  //           sx: {
                  //             py: 1,
                  //             borderRadius: 2, 
                  //             color: 'common.white', 
                  //             overflow: 'hidden', 
                  //             '& .MuiMenuItem-root': { 
                  //               typography: 'body2', 
                  //               height: '40px', 
                  //               lineHeight: '40px', 
                  //               transition: 'background-color 0.2s ease, color 0.2s ease', 
                  //               '&:hover': { 
                  //                 bgcolor: 'point.50', 
                  //                 color: 'primary.main', 
                  //               }, 
                  //               '&.Mui-selected': { 
                  //                 color: 'primary.main', 
                  //                 bgcolor: 'transparent', 
                  //               },
                  //             },
                  //           },
                  //         },
                  //       }}
                  //     >
                  //       <MenuItem onClick={() => navigate(user?.clss === 'PROFESSOR' ? '/mypage/management':'/mypage/courses')}>마이페이지</MenuItem>
                  //       <MenuItem onClick={handleLogout}>로그아웃</MenuItem>
                  //     </Select>
                  //   </FormControl>
                    
                  //   {userRole.role?.length > 0 ? (
                  //   <FormControl>
                  //     <Select
                  //       value={roleValue}
                  //       onChange={(e) => setRoleValue(e.target.value)}
                  //       sx={{
                  //         height: '40px',
                  //         color: 'common.white',
                  //         bgcolor: 'transparent !important',
                  //         '& .MuiSelect-select': {
                  //           px: 0,
                  //           color: 'common.white',
                  //           bgcolor: 'transparent !important',
                  //           display: 'flex',
                  //           alignItems: 'center',
                  //           height: '40px',
                  //           borderColor: 'transparent !important',
                  //           typography: 'body2',
                  //         },
                  //         '& .MuiSvgIcon-root': {
                  //           color: 'common.white',
                  //           fontSize: '20px',
                  //           right: 0,
                  //         },
                  //         '& fieldset': {
                  //           borderColor: 'transparent !important',
                  //         },
                  //         '&.Mui-expanded': { 
                  //           color: theme => `${theme.palette.text.contrastText} !important`, 
                  //           '& .MuiSelect-select': { 
                  //             color: 'primary.main' 
                  //           }, 
                  //           '& .MuiSvgIcon-root': { 
                  //           color: theme => `${theme.palette.text.contrastText} !important`, 
                  //             transform: 'rotate(180deg)', 
                  //           }, 
                  //         },
                  //       }}
                  //       MenuProps={{
                  //         PaperProps: {
                  //           sx: {
                  //             py: 1,
                  //             borderRadius: 2, 
                  //             color: 'common.white', 
                  //             overflow: 'hidden', 
                  //             '& .MuiMenuItem-root': { 
                  //               typography: 'body2', 
                  //               height: '40px', 
                  //               lineHeight: '40px', 
                  //               transition: 'background-color 0.2s ease, color 0.2s ease', 
                  //               '&:hover': { 
                  //                 bgcolor: 'point.50', 
                  //                 color: 'primary.main', 
                  //               }, 
                  //               '&.Mui-selected': { 
                  //                 color: 'primary.main', 
                  //                 bgcolor: 'transparent', 
                  //               },
                  //             },
                  //           },
                  //         },
                  //       }}
                  //     >
                  //       {userRole.role.map((role, idx) => (
                  //         <MenuItem key={idx} value={role}>
                  //           {role}
                  //         </MenuItem>
                  //       ))}
                  //     </Select>
                  //   </FormControl>
                  //   ) : (
                  //   <></>
                  //   )}

                  // </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <MLink
                        component={RouterLink}
                        to={buildLangHref('/login', lang)}
                        underline="none"
                        sx={{ flex: 1, textAlign: 'center', typography: 'body2', color: 'common.white', '&:hover': { color: 'primary.main' } }}
                      >
                        로그인
                      </MLink>
                      <Box
                        sx={{
                          width: '1px',
                          height: '13px',
                          bgcolor: 'divider',
                        }}
                      />
                      <MLink
                        component={RouterLink}
                        // to="/signup"
                        onClick={() => (window.location.href = buildLangHref("/signup", lang))}
                        underline="none"
                        sx={{ flex: 1, textAlign: 'center', typography: 'body2', color: 'common.white', '&:hover': { color: 'primary.main' } }}
                      >
                        회원가입
                      </MLink>
                    </Stack>
                )}
              </Box>
            </Box>

            {/* 1depth */}
            {sitemapItems
            .filter(root => {
              if (root.label === '마이페이지' && !userRole.isLoggedIn) return false
              return !root.hiddenInSitemap
            })
            .map((root, i) => (
              <Box key={i} sx={{ mb: isMobile ? 0 : 8 }}>
                {/* PC */}
                <Box sx={{ display: isMobile ? 'none' : 'block' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MLink
                      component={root.target === '_blank' ? 'a' : RouterLink}
                      href={root.target === '_blank' ? root.href : undefined}
                      to={root.target !== '_blank' ? buildLangHref(getEffectiveHref(root), lang) : undefined}
                      target={root.target === '_blank' ? '_blank' : undefined}
                      rel={root.target === '_blank' ? 'noopener noreferrer' : undefined}
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        typography: 'subtitle1',
                        mb: 2,
                        color: 'text.primary',
                        '&:hover': { color: 'primary.main' },
                      }}
                      onClick={() => {
                        clearSessionOnNavigate()
                        setSitemapOpen(false)
                      }}
                    >
                      {root.label}

                      {root.target === '_blank' && (
                        <OpenInNewIcon
                          sx={{
                            color: 'grey.dark',
                          }}
                        />
                      )}
                    </MLink>
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 1fr)',
                      gap: 6, 
                    }}
                  >
                    {(root.children || [])
                      .filter((l2) => !l2.hidden)
                      .map((l2, i2) => (
                        <Box
                          key={i2}
                          sx={{
                            mb: 1,
                            '&:hover > .l2-link': {
                              color: 'primary.main',
                              fontWeight: 600,
                            }
                          }}
                        >
                          {/* 2Depth */}
                          <MLink
                            className="l2-link"
                            component={l2.target === '_blank' ? 'a' : RouterLink}
                            href={l2.target === '_blank' ? l2.href : undefined}
                            to={l2.target !== '_blank' ? buildLangHref(getEffectiveHref(l2), lang) : undefined}
                            target={l2.target === '_blank' ? '_blank' : undefined}
                            rel={l2.target === '_blank' ? 'noopener noreferrer' : undefined}
                            onClick={() => {
                              clearSessionOnNavigate()
                              setSitemapOpen(false)
                            }}
                            underline="none"
                            sx={{
                              display: 'inline-block',
                              fontWeight: 500,
                              color: 'text.primary',
                              transition: 'color .2s ease',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {l2.label}
                          </MLink>
                          {Array.isArray(l2.children) && l2.children.length > 0 && (
                            <Box sx={{ mt: 1, mb: 3 }}>
                              {l2.children
                                .filter((l3) => !l3.hidden)
                                .map((l3, i3) => (
                                  <MLink
                                    key={i3}
                                    component={RouterLink}
                                    to={buildLangHref(getEffectiveHref(l2), lang)}
                                    onClick={() => {
                                      clearSessionOnNavigate()
                                      setSitemapOpen(false)
                                    }}
                                    underline="none"
                                    sx={{
                                      display: 'block',
                                      typography: 'body2',
                                      color: 'text.secondary',
                                      mt: 1,
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                  >
                                    {l3.label}
                                  </MLink>
                                ))}
                            </Box>
                          )}
                        </Box>
                    ))}
                  </Box>
                </Box>

                {/* 모바일(아코디언) */}
                <Box sx={{ display: isMobile ? 'block' : 'none', }}>
                  {Array.isArray(root.children) && root.children.length > 0 ? (
                    <Accordion 
                      disableGutters 
                      elevation={0}
                      expanded={mobileActiveIndex === i}
                      onChange={() =>
                        setMobileActiveIndex(mobileActiveIndex === i ? null : i)
                      }
                    >
                      <AccordionSummary
                        expandIcon={<KeyboardArrowDownIcon />}
                        aria-controls={`sitemap-${i}-content`}
                        id={`sitemap-${i}-header`}
                        sx={{
                          typography: 'body1',
                          fontWeight: '600',
                          px: 2,
                          height: '72px',
                          lineHeight: '72px',
                          '& .MuiAccordionSummary-content': { my: 0 },
                          '& .Mui-expanded': {
                            color: 'primary.main'
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'grey.dark',
                            transition: 'transform 0.2s ease',
                          },
                        }}
                      >
                        {root.label}
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, bgcolor: 'primary.50' }}>
                        {root.children
                          .filter((l2) => !l2.hidden)
                          .map((l2, i2) => (
                            <Box key={i2}>
                              <MLink
                                component={RouterLink}
                                to={buildLangHref(getEffectiveHref(l2), lang)}
                                onClick={() => {
                                  clearSessionOnNavigate()
                                  setSitemapOpen(false)
                                }}
                                underline="none"
                                color="text.primary"
                                sx={{
                                  display: 'block',
                                  typography: 'body2',
                                  height: '55px',
                                  lineHeight: '55px',
                                  px: 1,
                                }}
                              >
                                {l2.label}
                              </MLink>
                              {Array.isArray(l2.children) && l2.children.length > 0 && (
                                <Box>
                                  {l2.children.map((l3, i3) => (
                                    <MLink
                                      key={i3}
                                      component={RouterLink}
                                      to={buildLangHref(getEffectiveHref(l3), lang)}
                                      onClick={() => {
                                        clearSessionOnNavigate()
                                        setSitemapOpen(false)
                                      }}
                                      underline="none"
                                      sx={{
                                        display: 'block',
                                        typography: 'body2',
                                        height: '47px',
                                        lineHeight: '47px',
                                        px: 2,
                                      }}
                                    >
                                      {l3.label}
                                    </MLink>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <MLink
                      component={root.target === '_blank' ? 'a' : RouterLink}
                      href={root.target === '_blank' ? root.href : undefined}
                      to={root.target !== '_blank' ? buildLangHref(getEffectiveHref(root), lang) : undefined}
                      target={root.target === '_blank' ? '_blank' : undefined}
                      rel={root.target === '_blank' ? 'noopener noreferrer' : undefined}
                      underline="none"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        typography: 'body1',
                        fontWeight: '600',
                        px: 2,
                        height: '72px',
                        lineHeight: '72px',
                      }}
                      onClick={() => setSitemapOpen(false)}
                    >
                      {root.label}

                      {root.target === '_blank' && (
                        <OpenInNewIcon
                          sx={{
                            color: 'grey.dark',
                            fontSize: '20px',
                          }}
                        />
                      )}
                    </MLink>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Fade>
    </>
  )
}
